<template>
  <div>
    <!-- Study input picker modal -->
    <div v-if="daPicker" class="fixed inset-0 z-40 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/30" @click="daPicker = null" />
      <div class="relative z-10 bg-white rounded-lg shadow-xl w-[56rem] max-w-[90vw] h-[80vh] flex flex-col">
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-900">{{ daPicker.title }}</span>
            <button class="text-gray-400 hover:text-gray-600" @click="daPicker = null">&times;</button>
          </div>
          <p class="text-xs text-gray-500 mt-0.5">Choose a saved Dataset, a direct DataAttribute, or a saved frame.</p>
          <input
            ref="daSearchInput"
            v-model="daSearch"
            placeholder="Search inputs..."
            class="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          />
        </div>
        <div class="overflow-y-auto flex-1">
          <div class="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 sticky top-0 bg-white">
            <span>Kind</span><span>Name</span><span>Description</span>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="option in inputPickerOptions"
              :key="option.source_type + '-' + option.id"
              class="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 cursor-pointer items-baseline hover:bg-gray-50"
              @click="pickInput(option)"
            >
              <span class="text-xs text-gray-500">{{ sourceLabel(option.source_type) }}</span>
              <span class="sensitive text-xs text-gray-900 whitespace-normal break-words"><span class="sensitive">{{ option.name }}</span><span v-if="option.source_type === 'saved'" class="text-gray-400"> &middot; <span class="sensitive">{{ option.row_count.toLocaleString() }}</span> rows</span></span>
              <span class="sensitive text-xs text-gray-500 whitespace-normal break-words" data-demo-blur>{{ option.description || '' }}</span>
            </div>
          </div>
          <div v-if="!inputPickerOptions.length" class="px-4 py-8 text-center text-sm text-gray-400">No matches</div>
        </div>
      </div>
    </div>

    <!-- The study — script front and center, frame/params beside it -->
    <div class="space-y-4 min-w-0">
      <!-- Top bar -->
      <div class="flex items-center gap-3">
        <input
          v-model="config.name"
          type="text"
          placeholder="Study name..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium"
        />
        <span class="text-xs font-mono px-2 py-1 bg-gray-900 text-gray-100 rounded">R</span>
        <span v-if="config.template_key" class="text-xs font-mono px-2 py-1 bg-gray-100 text-gray-700 rounded">{{ config.template_key }}</span>
        <span v-if="config.is_builtin" class="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Built-in</span>
        <span v-if="saveError" class="text-xs text-red-600 max-w-xs truncate" :title="saveError">{{ saveError }}</span>
        <span v-if="hasAnyErrors" class="text-xs text-red-600">Validation errors</span>
        <button class="btn-secondary-sm text-xs whitespace-nowrap" :disabled="saving || !config.name" @click="saveStudy">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
        <button
          :disabled="!canRun || running || hasAnyErrors"
          class="btn-primary-sm text-xs whitespace-nowrap"
          @click="runStudy"
        >
          {{ running ? runPhase : 'Run Study' }}
        </button>
      </div>

      <div class="flex gap-4 items-start">
        <!-- Script: the study itself -->
        <div class="flex-1 min-w-0">
          <div class="border border-gray-300 rounded overflow-hidden">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-900">
              <span class="text-xs font-mono text-gray-400">script.R</span>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500 hidden lg:inline">ks_dataset() &middot; ks_frame() &middot; ks_saved() &middot; ks_params() &middot; ks_effect() &middot; ks_plot() &middot; ks_save()</span>
                <select v-model="selectedTemplate" class="px-2 py-0.5 border border-gray-700 bg-gray-800 text-gray-200 rounded text-xs">
                  <option value="">Templates...</option>
                  <option v-for="t in templates" :key="t.key" :value="t.key">{{ t.key }}</option>
                </select>
                <button @click="applyTemplate" :disabled="!selectedTemplate" class="text-xs px-2 py-0.5 bg-primary-500 hover:bg-primary-600 text-white rounded">Apply</button>
              </div>
            </div>
            <textarea
              v-model="config.script"
              rows="30"
              spellcheck="false"
              class="w-full px-4 py-3 bg-gray-900 text-gray-100 text-xs font-mono leading-relaxed focus:outline-none resize-y"
              placeholder="# The study's R script.
# Reads saved Datasets via ks_dataset(), direct DataAttributes via ks_frame(), saved frames via ks_saved().
# Emits results via ks_effect() / ks_table() / ks_plot() / ks_diagnostic(); keeps a table with ks_save().
# Pick a template above to start."
            ></textarea>
          </div>
        </div>

        <!-- Frame & Params: what the script consumes -->
        <div class="w-80 shrink-0 space-y-3">
          <div v-if="!frameSpecs.length && !paramRows.length" class="border border-gray-200 rounded p-3 text-xs text-gray-400">
            The script defines what appears here — its ks_frame() declarations and its p &lt;- list(...) block generate the Frame and Params panels, and edits here rewrite the script.
          </div>
          <div v-for="spec in frameSpecs" :key="spec.source_type + '-' + spec.slug" class="border border-gray-200 rounded p-3 space-y-1.5">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-semibold text-gray-700">{{ spec.slug }}.csv</span>
              <span class="text-[10px] uppercase text-gray-400">{{ sourceLabel(spec.source_type) }}</span>
            </div>
            <button
              class="w-full px-1.5 py-1 border border-gray-300 rounded text-[11px] text-left whitespace-normal break-words bg-white hover:border-gray-400"
              :class="spec.found ? 'text-gray-900' : 'border-red-300 bg-red-50 text-red-700'"
              :title="spec.found ? spec.source_name : `no ${spec.source_type} matches '${spec.slug}'`"
              @click="openInputPicker(spec)"
            >{{ spec.source_name || spec.slug }}</button>
            <div v-if="spec.columns.length" class="flex flex-wrap gap-1">
              <code v-for="col in spec.columns" :key="col" class="text-[10px] text-gray-500">{{ col }}</code>
            </div>
          </div>

          <div v-if="paramRows.length" class="border border-gray-200 rounded p-3 space-y-3">
            <div class="text-xs font-semibold text-gray-700">Params <span class="font-normal text-gray-400">&middot; the script's p &lt;- list(...)</span></div>
            <div v-for="param in paramRows" :key="param.name" class="flex items-center gap-2">
              <code class="text-[11px] text-gray-700 w-28 shrink-0 truncate" :title="'p$' + param.name">{{ param.name }}</code>
              <input
                v-if="param.raw === 'TRUE' || param.raw === 'FALSE'"
                type="checkbox"
                :checked="param.raw === 'TRUE'"
                @change="setParamValue(param.name, $event.target.checked ? 'TRUE' : 'FALSE')"
                class="rounded"
              />
              <input
                v-else
                :value="param.raw"
                @change="setParamValue(param.name, $event.target.value)"
                class="flex-1 min-w-0 px-1.5 py-1 border border-gray-300 rounded text-[11px] font-mono"
                title="an R literal: 5, &quot;text&quot;, TRUE, c(age = 5, ...)"
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

const SOURCE_LABELS = { dataset: 'Dataset', attribute: 'DataAttribute', saved: 'Saved frame' };
const SOURCE_FUNCTIONS = { dataset: 'ks_dataset', attribute: 'ks_frame', saved: 'ks_saved' };

export default {
  name: 'StudyEditor',
  props: {
    attributes: {
      type: Array,
      default: () => []
    },
    datasets: {
      type: Array,
      default: () => []
    },
    frames: {
      type: Array,
      default: () => []
    },
    kind: {
      type: String,
      default: 'study'
    },
    studyId: {
      type: Number,
      default: null
    }
  },
  emits: ['saved', 'ran'],
  data() {
    return {
      templates: [],
      selectedTemplate: '',
      selectedStudyId: null,
      loadedScript: '',
      loading: true,
      running: false,
      runPhase: 'Running...',
      saving: false,
      saveError: null,
      frameSpecs: [],
      frameSpecTimer: null,
      daPicker: null,
      daSearch: '',

      config: this.emptyConfig(),
    };
  },
  computed: {
    canRun() {
      return Boolean(this.config.script);
    },
    hasAnyErrors() {
      return this.frameSpecs.some(s => !s.found);
    },
    inputPickerOptions() {
      if (!this.daPicker) return [];
      const options = [
        ...this.datasets.map((dataset) => ({ ...dataset, source_type: 'dataset' })),
        ...this.attributes.map((attribute) => ({ ...attribute, source_type: 'attribute' })),
        ...this.frames.map((frame) => ({ ...frame, source_type: 'saved' })),
      ];
      const q = this.daSearch.trim().toLowerCase();
      if (!q) return options;
      return options.filter(option =>
        option.name.toLowerCase().includes(q) ||
        (option.description || '').toLowerCase().includes(q));
    },
    paramRows() {
      const script = this.config.script || '';
      const m = /\bp\s*<-\s*list\s*\(/g.exec(script);
      if (!m) return [];
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return [];
      const body = script.slice(m.index + span.start, m.index + span.end);
      const rows = [];
      const re = /([\w.]+)\s*=\s*("(?:[^"\\]|\\.)*"|c\([^)]*\)|[^,\n)]+)/g;
      let am;
      while ((am = re.exec(body))) {
        rows.push({ name: am[1], raw: am[2].trim() });
      }
      return rows;
    },
  },
  watch: {
    'config.script'() {
      if (this.frameSpecTimer) clearTimeout(this.frameSpecTimer);
      this.frameSpecTimer = setTimeout(this.fetchFrameSpecs, 600);
    },
  },
  methods: {
    emptyConfig() {
      return {
        name: '',
        is_builtin: false,
        script: '',
        template_key: '',
      };
    },
    savePayload() {
      return {
        name: this.config.name,
        kind: this.kind,
        script: this.config.script,
        script_language: 'r',
        template_key: this.config.template_key,
      };
    },
    async saveStudy() {
      this.saving = true;
      this.saveError = null;
      try {
        let response;
        if (this.selectedStudyId) {
          response = await axios.put(`api/impact_analytics/studies/${this.selectedStudyId}/`, this.savePayload());
        } else {
          response = await axios.post('api/impact_analytics/studies/', this.savePayload());
          this.selectedStudyId = response.data.id;
        }
        this.loadedScript = this.config.script;
        this.$emit('saved', response.data);
        return true;
      } catch (error) {
        const detail = error.response?.data;
        this.saveError = typeof detail === 'object'
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ').slice(0, 200)
          : 'Save failed';
        return false;
      } finally {
        this.saving = false;
      }
    },
    async fetchTemplates() {
      try {
        const response = await axios.get('api/impact_analytics/study-templates/');
        this.templates = response.data.filter((t) => (t.kind || 'study') === this.kind);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    },
    applyTemplate() {
      const t = this.templates.find(x => x.key === this.selectedTemplate);
      if (!t) return;
      if (this.config.script && this.config.script !== t.script) {
        const current = this.templates.find(x => x.key === this.config.template_key);
        const edited = !current || this.config.script !== current.script;
        if (edited && !confirm(`Replace the current script with the ${t.key} template? Unsaved edits to the script will be lost.`)) {
          return;
        }
      }
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    sourceLabel(sourceType) {
      return SOURCE_LABELS[sourceType] || 'DataAttribute';
    },
    newStudy() {
      this.selectedStudyId = null;
      this.config = this.emptyConfig();
      this.paramsError = null;
    },
    async loadStudy(study) {
      this.selectedStudyId = study.id;

      // Fetch full study details
      try {
        const response = await axios.get(`api/impact_analytics/studies/${study.id}/`);
        this.loadedScript = response.data.script;
        const s = response.data;

        this.config = {
          name: s.name,
          is_builtin: s.is_builtin,
          script: s.script || '',
          template_key: s.template_key || '',
        };

        this.fetchFrameSpecs();
      } catch (error) {
        console.error('Error loading study details:', error);
      }
    },
    openInputPicker(spec) {
      this.daPicker = {
        title: `Input ${spec.slug}.csv`,
        slug: spec.slug,
        source_type: spec.source_type,
      };
      this.daSearch = '';
      this.$nextTick(() => this.$refs.daSearchInput?.focus());
    },
    pickInput(option) {
      const newSlug = this.slugifyName(option.name);
      this.setInput(this.daPicker, option.source_type, newSlug);
      this.daPicker = null;
    },
    slugifyName(name) {
      const slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      return /^[0-9]/.test(slug) ? `x${slug}` : slug;
    },
    setInput(current, sourceType, newSlug) {
      const currentFunction = SOURCE_FUNCTIONS[current.source_type] || 'ks_frame';
      const nextFunction = SOURCE_FUNCTIONS[sourceType] || 'ks_frame';
      const pattern = new RegExp(`(${currentFunction}\\(\\s*")${current.slug}(")`);
      this.config.script = this.config.script.replace(
        pattern,
        `${nextFunction}("${newSlug}"`,
      );
    },
    findCallSpan(script, headRe) {
      const m = headRe.exec(script);
      if (!m) return null;
      let i = m.index + m[0].length;
      let depth = 1;
      let quote = null;
      const start = i;
      while (i < script.length && depth) {
        const ch = script[i];
        if (quote) {
          if (ch === quote && script[i - 1] !== '\\') quote = null;
        } else if (ch === '"' || ch === "'") {
          quote = ch;
        } else if (ch === '(') {
          depth += 1;
        } else if (ch === ')') {
          depth -= 1;
        }
        i += 1;
      }
      return depth === 0 ? { start, end: i - 1 } : null;
    },
    setParamValue(name, raw) {
      const script = this.config.script;
      const head = /\bp\s*<-\s*list\s*\(/g;
      const m = head.exec(script);
      if (!m) return;
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return;
      const abs = { start: m.index + span.start, end: m.index + span.end };
      const body = script.slice(abs.start, abs.end);
      const re = new RegExp(`(\\b${name}\\s*=\\s*)("(?:[^"\\\\]|\\\\.)*"|c\\([^)]*\\)|[^,\\n)]+)`);
      if (!re.test(body)) return;
      const newBody = body.replace(re, `$1${raw}`);
      this.config.script = script.slice(0, abs.start) + newBody + script.slice(abs.end);
    },
    async fetchFrameSpecs() {
      if (!this.config.script) {
        this.frameSpecs = [];
        return;
      }
      try {
        const response = await axios.post('api/impact_analytics/study-frame-spec/', {
          script: this.config.script,
        });
        this.frameSpecs = response.data;
      } catch (error) {
        console.error('Error resolving frame spec:', error);
      }
    },
    getAttributeName(id) {
      const attr = this.attributes.find(a => a.id === id);
      return attr ? attr.name : '';
    },
    async runStudy() {
      if (!this.canRun || this.hasAnyErrors) return;
      this.running = true;
      this.runPhase = 'Saving...';

      if (this.selectedStudyId && this.config.script === this.loadedScript) {
        // Not edited locally: adopt the server's current script instead of
        // overwriting it with a possibly stale browser copy.
        try {
          const detail = await axios.get(`api/impact_analytics/studies/${this.selectedStudyId}/`);
          this.config.script = detail.data.script;
          this.loadedScript = detail.data.script;
        } catch (error) {
          console.error('Error refreshing study script:', error);
        }
      } else {
        const saved = await this.saveStudy();
        if (!saved) {
          this.running = false;
          return;
        }
      }

      this.runPhase = 'Running...';
      try {
        const response = await axios.post(`api/impact_analytics/studies/${this.selectedStudyId}/run/`, {});
        this.$emit('ran', response.data.id);
      } catch (error) {
        console.error('Error running study:', error);
        this.saveError = error.response?.data?.error || error.message;
      } finally {
        this.running = false;
      }
    },
  },
  async mounted() {
    await this.fetchTemplates();
    if (this.studyId) this.loadStudy({ id: this.studyId });
    else this.newStudy();
  },
};
</script>
